'use client';

/**
 * CanadaHero — the `.se-hero` band from the Canada homepage handoff.
 *
 * Same dark editorial surface as the V3 `/private` hero, but the handoff's
 * Canada layout puts the platform stats (`.se-hero-stats`) in the right column
 * instead of the New Portfolio card, and the headline carries the footnote
 * marker that ties to the ticker disclosure below it.
 */
import type { MouseEvent } from 'react';
import * as Sentry from 'utils/errorTracking';
import { styled } from '@mui/material/styles';
import { trackEventWithGTM } from 'utils/analytics';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import type { HeroStat } from 'components/HomepageV3/data/demoData';
import { CANADA_HERO, CANADA_HERO_STATS } from '../data/canadaContent';

export interface CanadaHeroProps {
  stats?: HeroStat[];
  onCtaClick?: (event: MouseEvent<HTMLElement>) => void;
  className?: string;
  'data-cy'?: string;
}

const Band = styled('section')({
  background: 'radial-gradient(120% 90% at 50% 0%, #0D2A2C 0%, #07191A 46%, #030D0C 100%)',
  color: '#fff',
});

const Grid = styled('div')({
  maxWidth: 1280,
  margin: '0 auto',
  padding: '40px var(--pad) 36px',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '32px',
  alignItems: 'center',
  '@media (min-width: 1024px)': {
    gridTemplateColumns: 'minmax(0, 1fr) 440px',
    gap: '60px',
    padding: '74px var(--pad) 58px',
  },
});

const Title = styled('h1')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: '34px',
  lineHeight: 1.08,
  letterSpacing: '-0.012em',
  color: '#fff',
  textWrap: 'pretty',
  margin: 0,
  '& em': {
    fontStyle: 'italic',
    color: COLORS.champagne,
  },
  '@media (min-width: 1024px)': {
    fontSize: '54px',
    maxWidth: '14ch',
  },
});

const Footnote = styled('sup')({
  fontFamily: FONTS.sans,
  fontStyle: 'normal',
  fontSize: '0.42em',
  fontWeight: 600,
  color: 'rgba(233,215,182,0.75)',
  verticalAlign: 'super',
  marginLeft: '2px',
});

const Rule = styled('div')({
  width: '96px',
  height: '1px',
  background: COLORS.gold,
  margin: '34px 0 0',
});

const Cta = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  marginTop: '34px',
  flexWrap: 'wrap',
});

const CtaBtn = styled('button')({
  fontFamily: FONTS.sans,
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: COLORS.night,
  background: COLORS.cream,
  border: 'none',
  padding: '18px 42px',
  borderRadius: '16px',
  cursor: 'pointer',
  boxShadow: '0 18px 40px rgba(0,0,0,0.3)',
  transition: 'transform 0.16s, background 0.16s',
  '&:hover': { background: '#fff', transform: 'translateY(-2px)' },
});

const CtaNote = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '13px',
  color: 'rgba(255,255,255,0.62)',
  letterSpacing: '0.02em',
  lineHeight: 1.5,
  '& span': {
    display: 'block',
    color: 'rgba(233,215,182,0.7)',
  },
});

const Stats = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '1px',
  background: 'rgba(233,215,182,0.18)',
  border: '1px solid rgba(233,215,182,0.18)',
  borderRadius: '20px',
  overflow: 'hidden',
});

const Stat = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '26px 24px',
  background: 'linear-gradient(160deg, rgba(252,249,245,0.07), rgba(252,249,245,0.02))',
  '@media (max-width: 640px)': {
    padding: '20px 18px',
  },
});

const StatVal = styled('span')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: '32px',
  lineHeight: 1.1,
  color: '#fff',
  '@media (max-width: 640px)': {
    fontSize: '26px',
  },
});

const StatLabel = styled('span')({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  lineHeight: 1.4,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.62)',
});

export function CanadaHero({
  stats = CANADA_HERO_STATS,
  onCtaClick,
  className,
  'data-cy': dataCy = 'canada-hero',
}: CanadaHeroProps) {
  const handleCtaClick = (event: MouseEvent<HTMLElement>) => {
    try {
      trackEventWithGTM('canada_landing_hero_cta_clicked', {
        page: 'canada_landing_page',
        surface: 'canada_hero',
        cta: CANADA_HERO.ctaLabel,
      });
    } catch (e) {
      Sentry.captureException(e, { tags: { area: 'canada_landing_page' } });
    }
    onCtaClick?.(event);
  };

  return (
    <Band className={`se-hero ${className ?? ''}`.trim()} data-cy={dataCy}>
      <Grid>
        <div>
          <Title data-cy="canada-hero-title">
            {CANADA_HERO.title}
            <em>
              {CANADA_HERO.titleEm}
              <Footnote>{CANADA_HERO.footnoteMarker}</Footnote>
            </em>
          </Title>
          <Rule />
          <Cta>
            <CtaBtn type="button" onClick={handleCtaClick} data-cy="canada-hero-cta">
              {CANADA_HERO.ctaLabel}
            </CtaBtn>
            <CtaNote data-cy="canada-hero-cta-note">
              {CANADA_HERO.ctaNote}
              <span>{CANADA_HERO.ctaNoteSub}</span>
            </CtaNote>
          </Cta>
        </div>

        <Stats className="se-hero-stats" data-cy="canada-hero-stats">
          {stats.map((stat) => (
            <Stat key={stat.label} data-cy="canada-hero-stat">
              <StatVal>{stat.val}</StatVal>
              <StatLabel>{stat.label}</StatLabel>
            </Stat>
          ))}
        </Stats>
      </Grid>
    </Band>
  );
}

export default CanadaHero;
